import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { cn } from 'ui/lib/utils';

interface MegaMenuColumn {
  title?: string;
  links: { label: string; count?: number; href: string }[];
  /** Render `rows` skeleton placeholders instead of links while data loads. */
  loading?: boolean;
  rows?: number;
}

interface MegaMenuFeature {
  label: string;
  title: string;
  price: number;
  originalPrice?: number;
  image: React.ReactNode;
  href: string;
}

interface MegaMenuData {
  columns: MegaMenuColumn[];
  feature?: MegaMenuFeature;
  /** Reserve + skeleton the feature column while the listing loads. */
  featureLoading?: boolean;
  align?: 'left' | 'right';
}

interface MegaMenuProps extends MegaMenuData {
  className?: string;
}

// Deterministic pseudo-random widths so skeleton rows look natural without
// shifting between renders.
const SKELETON_WIDTHS = ['82%', '66%', '74%', '60%', '78%', '69%'];

/** `$1,234` for a real number, `null` for anything else (absent, null, NaN). */
function formatMenuPrice(value: number | undefined): string | null {
  return typeof value === 'number' && Number.isFinite(value)
    ? `$${value.toLocaleString()}`
    : null;
}

/**
 * Ported from gearfocus-v2's marketplace MegaMenu. rem utilities → px for
 * gearfocus-web's 10px rem root; the shadcn Button CTA is replaced with a
 * styled Link so it sizes correctly. Columns/feature can render skeletons so the
 * menu keeps a stable width/height while async content streams in.
 */
const MegaMenu = ({
  columns,
  feature,
  featureLoading,
  align = 'left',
  className,
}: MegaMenuProps) => {
  const hasFeatureCol = Boolean(feature) || Boolean(featureLoading);

  return (
    <div
      data-slot="mega-menu"
      data-align={align}
      className={cn(
        'w-max max-w-[calc(100vw-32px)] rounded-[10px] bg-white p-[24px]',
        className
      )}
    >
      <div
        className="grid gap-[44px]"
        style={{
          gridTemplateColumns: `repeat(${columns.length}, 210px)${
            hasFeatureCol ? ' 240px' : ''
          }`,
        }}
      >
        {columns.map((col, colIndex) => (
          <div key={col.title ?? colIndex} className="flex flex-col">
            {col.title && (
              <h3 className="mb-[12px] border-b border-gray-100 pb-[8px] text-[12px] font-semibold uppercase tracking-[0.6px] text-gray-500">
                {col.title}
              </h3>
            )}
            <ul
              className="flex flex-col gap-[2px]"
              style={{ listStyle: 'none', margin: 0, paddingInlineStart: 0 }}
            >
              {col.loading
                ? Array.from({ length: col.rows ?? 5 }).map((_, i) => (
                    <li key={i} className="px-[8px] py-[6px]">
                      <span
                        className="block h-[14px] animate-pulse rounded bg-gray-100"
                        style={{ width: SKELETON_WIDTHS[i % SKELETON_WIDTHS.length] }}
                      />
                    </li>
                  ))
                : col.links.map((link) => (
                    <li key={link.label}>
                      <Link
                        href={link.href}
                        className="-mx-[8px] flex items-center justify-between gap-[8px] rounded-sm px-[8px] py-[6px] text-[14px] text-gray-800 transition-colors hover:bg-primary-light hover:text-primary"
                      >
                        <span className="whitespace-normal">{link.label}</span>
                      </Link>
                    </li>
                  ))}
            </ul>
          </div>
        ))}

        {feature ? (
          <div className="flex flex-col rounded-[10px] bg-gray-50 p-[16px]">
            <div className="mb-[12px] flex aspect-[16/10] items-center justify-center overflow-hidden rounded-sm border border-gray-200 bg-white p-[12px]">
              {feature.image}
            </div>
            <span className="mb-[8px] inline-block self-start rounded-sm bg-primary-light px-[8px] py-[2px] text-[10px] font-bold uppercase tracking-[0.5px] text-primary">
              {feature.label}
            </span>
            <h4 className="mb-[8px] line-clamp-2 text-[14px] font-semibold leading-tight text-dark">
              {feature.title}
            </h4>
            {/* Same guard UIProductCard carries: dealer-feed listings
                occasionally arrive with a null/NaN price, and .toLocaleString()
                on that throws during render. This one sits in the header, so
                the throw took down every page, not one card. */}
            <div className="mb-[12px] flex items-baseline gap-[6px] text-[16px] font-bold text-dark tabular-nums">
              {formatMenuPrice(feature.price) ?? '—'}
              {formatMenuPrice(feature.originalPrice) && (
                <s className="text-[14px] font-normal text-gray-500">
                  {formatMenuPrice(feature.originalPrice)}
                </s>
              )}
            </div>
            <Link
              href={feature.href}
              className="mt-auto inline-flex h-[32px] w-full items-center justify-center gap-[6px] rounded-[10px] bg-primary text-[13px] font-semibold text-white transition-colors hover:bg-primary-hover"
            >
              View listing
              <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </div>
        ) : (
          featureLoading && (
            <div className="flex flex-col rounded-[10px] bg-gray-50 p-[16px]">
              <div className="mb-[12px] aspect-[16/10] animate-pulse rounded-sm border border-gray-200 bg-gray-100" />
              <span className="mb-[8px] h-[14px] w-[45%] animate-pulse rounded bg-gray-100" />
              <span className="mb-[6px] h-[14px] w-[90%] animate-pulse rounded bg-gray-100" />
              <span className="mb-[10px] h-[14px] w-[60%] animate-pulse rounded bg-gray-100" />
              <span className="mb-[12px] h-[16px] w-[35%] animate-pulse rounded bg-gray-100" />
              <span className="mt-auto h-[32px] w-full animate-pulse rounded-[10px] bg-gray-100" />
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default MegaMenu;
export type { MegaMenuData, MegaMenuColumn, MegaMenuFeature };
